import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import {
  Layout,
  PhotoMarker,
  Unit,
  Position,
  ImageDimensions,
  SaveMarkersResponse,
  SaveMarkersData,
} from '../types/types';
import TopControls from '../superadmin-controls/TopControls';
import MainContentArea from '../MainContentArea';
import { PDFLayoutViewer } from './PDFLayoutViewer';
import StatusBar from '../superadmin-controls/StatusBar';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';
import { EnhancedAreaTagsOverlay } from './EnhancedAreaTagsOverlay';
import { Button } from '@/shared/ui/button';
import { ArrowLeft, ZoomOut, ImagePlus } from 'lucide-react';
import { useProperty } from '@/context/PropertyContext';
import { patchAreaTag as apiPatchAreaTag } from '@/features/area-tags/api/area-tags-api';
import { areaTagKeys } from '@/features/area-tags/hooks/useAreaTags';
import { AddLayoutPhotoDialog } from './AddLayoutPhotoDialog';
import { useUser } from '@/shared/hooks/useUser';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { useDeleteAreaTag } from '@/features/area-tags/hooks/useAreaTagMutations';

const normalizePolygon = (points: number[][]): number[][] => {
  if (!points || points.length === 0) return [];
  const normalized = points.map((p) => [Number(p[0]), Number(p[1])]);
  if (normalized.length > 1) {
    const [fx, fy] = normalized[0];
    const [lx, ly] = normalized[normalized.length - 1];
    if (fx === lx && fy === ly) return normalized.slice(0, -1);
  }
  return normalized;
};

const polygonsOverlap = (polyA: number[][], polyB: number[][]): boolean => {
  const a = normalizePolygon(polyA);
  const b = normalizePolygon(polyB);
  if (a.length < 3 || b.length < 3) return false;

  const orient = (p: number[], q: number[], r: number[]) => {
    const v = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1]);
    if (Math.abs(v) < 1e-10) return 0;
    return v > 0 ? 1 : 2;
  };
  const onSeg = (p: number[], q: number[], r: number[]) =>
    q[0] <= Math.max(p[0], r[0]) + 1e-10 &&
    q[0] >= Math.min(p[0], r[0]) - 1e-10 &&
    q[1] <= Math.max(p[1], r[1]) + 1e-10 &&
    q[1] >= Math.min(p[1], r[1]) - 1e-10;
  const intersects = (p1: number[], q1: number[], p2: number[], q2: number[]) => {
    const o1 = orient(p1, q1, p2);
    const o2 = orient(p1, q1, q2);
    const o3 = orient(p2, q2, p1);
    const o4 = orient(p2, q2, q1);
    if (o1 !== o2 && o3 !== o4) return true;
    if (o1 === 0 && onSeg(p1, p2, q1)) return true;
    if (o2 === 0 && onSeg(p1, q2, q1)) return true;
    if (o3 === 0 && onSeg(p2, p1, q2)) return true;
    if (o4 === 0 && onSeg(p2, q1, q2)) return true;
    return false;
  };
  const pointOnSegment = (p: number[], a: number[], b: number[]) => {
    const cross = (p[1] - a[1]) * (b[0] - a[0]) - (p[0] - a[0]) * (b[1] - a[1]);
    if (Math.abs(cross) > 1e-10) return false;
    const dot = (p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1]);
    if (dot < -1e-10) return false;
    const lenSq = (b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2;
    if (dot - lenSq > 1e-10) return false;
    return true;
  };
  const strictIntersects = (p1: number[], q1: number[], p2: number[], q2: number[]) => {
    // Shared boundaries/vertices are allowed; only proper edge crossings count as overlap.
    if (!intersects(p1, q1, p2, q2)) return false;
    if (
      pointOnSegment(p1, p2, q2) ||
      pointOnSegment(q1, p2, q2) ||
      pointOnSegment(p2, p1, q1) ||
      pointOnSegment(q2, p1, q1)
    ) return false;
    return true;
  };
  const pipStrict = (point: number[], polygon: number[][]) => {
    for (let i = 0; i < polygon.length; i++) {
      if (pointOnSegment(point, polygon[i], polygon[(i + 1) % polygon.length])) {
        return false; // touching boundary is valid adjacency
      }
    }
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      const hit = (yi > point[1]) !== (yj > point[1]) &&
        point[0] < ((xj - xi) * (point[1] - yi)) / ((yj - yi) || 1e-12) + xi;
      if (hit) inside = !inside;
    }
    return inside;
  };

  for (let i = 0; i < a.length; i++) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (strictIntersects(a1, a2, b1, b2)) return true;
    }
  }

  return pipStrict(a[0], b) || pipStrict(b[0], a);
};

interface LayoutsViewProps {
  // Layout state
  selectedLayoutId: string;
  setSelectedLayoutId: (id: string) => void;
  layouts: Layout[];
  loadLayout: (layout: Layout) => void;
  layoutsLoading: boolean;
  layoutDataLoading: boolean;

  // Display data
  imagePath: string;
  pdfPath: string | null;
  pdfPageNumber: number;
  pdfCropRegion: [number, number, number, number] | null;
  isPdfLayout: boolean;
  imageDimensions: ImageDimensions;
  areaTags: Unit[];
  displayPhotoMarkers: PhotoMarker[];
  projectId: string;

  // Interaction handlers
  getCursor: () => string;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleSvgClick: (e: React.MouseEvent) => void;
  handleUnitClick: (unit: Unit, event: React.MouseEvent) => void;
  handleMarkerClick: (marker: PhotoMarker, event: React.MouseEvent) => void;
  deletePhotoMarker: (markerId: number) => void;
  handleMarkerRightClick?: (event: React.MouseEvent) => void;
  handleMarkerDragStart?: (markerId: number, event: React.MouseEvent) => void;
  onWheelZoom: (e: React.WheelEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  /** Called by PDFCanvasViewport when it auto-fits, so reset() can restore the fit view. */
  onPdfFitComplete?: (fitScale: number, fitPosition: Position) => void;
  // Needed by PDFLayoutViewer for click-to-place functionality
  addPhotoMarker: (point: Position) => void;
  addMeasurementPoint: (point: Position) => void;

  // Control functions
  zoom: (factor: number) => void;
  reset: () => void;
  mode: 'view' | 'measure' | 'photoIndex' | 'areaTags';
  setMode: (mode: 'view' | 'measure' | 'photoIndex' | 'areaTags') => void;
  photoIndexMode: boolean;
  setCurrentPolygon: (polygon: number[][]) => void;
  closePolygon: () => void;
  clearPolygon: () => void;
  savePhotoMarkers: () => void;
  setPhotoIndexMode: (value: boolean) => void;
  setSelectedMarkers: (markers: Set<number>) => void;
  setShowMarkerToolbar: (value: boolean) => void;
  setSelectedUnit: (unit: Unit | null) => void;
  setImageDimensions: (dimensions: ImageDimensions) => void;

  // SVG refs and state
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  position: Position;
  scale: number;
  setPosition: (position: Position) => void;
  setScale: (scale: number) => void;
  selectedUnit: Unit | null;
  currentPolygon: number[][];
  selectedMarkers: Set<number>;
  showCoordinates: boolean;

  // StatusBar specific props
  savePhotoMarkersMutation: UseMutationResult<SaveMarkersResponse, Error, SaveMarkersData, unknown>;
  isHorizontalConstrained: boolean;
  isVerticalConstrained: boolean;
  setShowCoordinates: (show: boolean) => void;
}

const LayoutsView: React.FC<LayoutsViewProps> = ({
  selectedLayoutId,
  setSelectedLayoutId,
  layouts,
  loadLayout,
  layoutsLoading,
  layoutDataLoading,
  imagePath,
  pdfPath,
  pdfPageNumber,
  pdfCropRegion,
  isPdfLayout,
  imageDimensions,
  areaTags,
  displayPhotoMarkers,
  projectId,
  getCursor,
  handleMouseDown,
  handleSvgClick,
  handleUnitClick,
  handleMarkerClick,
  deletePhotoMarker,
  handleMarkerRightClick,
  handleMarkerDragStart,
  onWheelZoom,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  onPdfFitComplete,
  addPhotoMarker,
  addMeasurementPoint,
  zoom,
  reset: resetFromParent,
  mode,
  setMode,
  photoIndexMode,
  setCurrentPolygon,
  closePolygon,
  clearPolygon,
  savePhotoMarkers,
  setPhotoIndexMode,
  setSelectedMarkers,
  setShowMarkerToolbar,
  setSelectedUnit,
  setImageDimensions,
  svgRef,
  groupRef,
  position,
  scale,
  setPosition,
  setScale,
  selectedUnit,
  currentPolygon,
  selectedMarkers,
  showCoordinates,
  savePhotoMarkersMutation,
  isHorizontalConstrained,
  isVerticalConstrained,
  setShowCoordinates,
}) => {
  const previousModeRef = useRef(mode);

  // Stores the auto-fit values when a PDF layout is loaded, so reset() restores the fit view.
  const pdfFitRef = useRef<{ scale: number; position: Position } | null>(null);
  const reset = useCallback(() => {
    const fit = pdfFitRef.current;
    if (isPdfLayout && fit) {
      setScale(fit.scale);
      setPosition(fit.position);
    } else {
      resetFromParent();
    }
  }, [isPdfLayout, resetFromParent, setScale, setPosition]);

  // Text editing state for area tags
  const [textEditingMode, setTextEditingMode] = useState(false);
  
  // Polygon editing state for area tags
  const [polygonEditingMode, setPolygonEditingMode] = useState(false);

  // Add layout photo dialog
  const [showAddPhotoDialog, setShowAddPhotoDialog] = useState(false);
  const [isLabelDialogOpen, setIsLabelDialogOpen] = useState(false);
  const [activeLabelAreaId, setActiveLabelAreaId] = useState<number | null>(null);
  const [draftLabel, setDraftLabel] = useState('');
  const [isSavingLabel, setIsSavingLabel] = useState(false);

    // Get project ID from property context
  const { selectedProperty } = useProperty();
  const contextProjectId = selectedProperty?.id || projectId;
  const queryClient = useQueryClient();
  const { user } = useUser();
  const isSuperuser = Boolean(user?.is_superuser);
  const deleteAreaTagMutation = useDeleteAreaTag(selectedLayoutId, contextProjectId);

  // Create a fresh mutation function that captures current values
  const performPatch = useCallback(async (areaTagId: number, data: any) => {
    // Use the direct API call with current values (not stale closures)
    return apiPatchAreaTag(areaTagId, data, selectedLayoutId, contextProjectId);
  }, [contextProjectId, selectedLayoutId]);

  // Handle text editing mode change from overlay
  const handleTextEditingModeChange = useCallback((isTextEditingMode: boolean) => {
    setTextEditingMode(isTextEditingMode);
  }, []);

  // Handle polygon editing mode change from overlay
  const handlePolygonEditingModeChange = useCallback((isPolygonEditingMode: boolean) => {
    setPolygonEditingMode(isPolygonEditingMode);
  }, []);

  // Local text editing state (not saved until user clicks save)
  const [pendingTextChanges, setPendingTextChanges] = useState<Map<number, Partial<{ name: string; text_position: [number, number]; text_size: number; text_rotation: number }>>>(new Map());
  
  // Local polygon editing state (not saved until user clicks save)
  const [pendingPolygonChanges, setPendingPolygonChanges] = useState<Map<number, number[][]>>(new Map());

  const getAreaLabel = useCallback(
    (area: Unit) => pendingTextChanges.get(area.id)?.name || Object.keys(area.images || {})[0] || `Area ${area.id}`,
    [pendingTextChanges]
  );

  const openLabelDialogForArea = useCallback(
    (areaId: number) => {
      const area = areaTags.find((item) => item.id === areaId);
      if (!area) return;
      setActiveLabelAreaId(areaId);
      setDraftLabel(getAreaLabel(area));
      setIsLabelDialogOpen(true);
    },
    [areaTags, getAreaLabel]
  );

  const closeLabelDialog = useCallback(() => {
    if (isSavingLabel) return;
    setIsLabelDialogOpen(false);
    setActiveLabelAreaId(null);
    setDraftLabel('');
  }, [isSavingLabel]);

  const handleLabelDialogSave = useCallback(async () => {
    if (!activeLabelAreaId) return;
    const nextName = draftLabel.trim();
    if (!nextName) {
      alert('Label cannot be empty.');
      return;
    }

    const previousEntry = pendingTextChanges.get(activeLabelAreaId);

    // Optimistic UI update
    setPendingTextChanges((prev) => {
      const next = new Map(prev);
      const existing = next.get(activeLabelAreaId) || {};
      next.set(activeLabelAreaId, { ...existing, name: nextName });
      return next;
    });
    setIsSavingLabel(true);

    try {
      await performPatch(activeLabelAreaId, { name: nextName });
      await queryClient.invalidateQueries({ queryKey: areaTagKeys.list(selectedLayoutId) });
      setIsLabelDialogOpen(false);
      setActiveLabelAreaId(null);
      setDraftLabel('');
    } catch (error) {
      // Roll back optimistic label change on failure
      setPendingTextChanges((prev) => {
        const next = new Map(prev);
        if (previousEntry) {
          next.set(activeLabelAreaId, previousEntry);
        } else {
          const current = next.get(activeLabelAreaId);
          if (current) {
            const { name: _removed, ...rest } = current;
            if (Object.keys(rest).length > 0) {
              next.set(activeLabelAreaId, rest);
            } else {
              next.delete(activeLabelAreaId);
            }
          }
        }
        return next;
      });
      alert('Failed to save label. Please try again.');
    } finally {
      setIsSavingLabel(false);
    }
  }, [activeLabelAreaId, draftLabel, pendingTextChanges, performPatch, queryClient, selectedLayoutId]);

  // Save all pending changes (both text and polygon)
  const handleSaveChanges = useCallback(async () => {
    const textChanges = Array.from(pendingTextChanges.entries());
    const polygonChanges = Array.from(pendingPolygonChanges.entries());
    
    if (textChanges.length === 0 && polygonChanges.length === 0) return;

    try {
      // Validate overlap locally before submit
      const resolvedPolygons = new Map<number, number[][]>();
      areaTags.forEach((tag) => {
        resolvedPolygons.set(tag.id, pendingPolygonChanges.get(tag.id) || tag.points);
      });
      for (let i = 0; i < areaTags.length; i++) {
        for (let j = i + 1; j < areaTags.length; j++) {
          const a = resolvedPolygons.get(areaTags[i].id) || [];
          const b = resolvedPolygons.get(areaTags[j].id) || [];
          if (polygonsOverlap(a, b)) {
            alert(`Cannot save polygon edits: "${Object.keys(areaTags[i].images || {})[0] || areaTags[i].id}" overlaps another area.`);
            return;
          }
        }
      }

      // Prepare all changes for saving
      const allChanges: Promise<any>[] = [];
      
      // Add text changes
      textChanges.forEach(([areaId, data]) => {
        allChanges.push(performPatch(areaId, data));
      });
      
      // Add polygon changes
      polygonChanges.forEach(([areaId, coordinates]) => {
        // Backend expects coordinates as array of arrays: [[x, y], [x, y], ...]
        allChanges.push(performPatch(areaId, { coordinates: coordinates }));
      });
      
      // Save all changes in parallel
      await Promise.all(allChanges);
      
      // Clear pending changes on success
      setPendingTextChanges(new Map());
      setPendingPolygonChanges(new Map());
      
      // Invalidate area tags cache to refetch fresh data
      queryClient.invalidateQueries({ queryKey: areaTagKeys.list(selectedLayoutId) });
      
      const totalChanges = textChanges.length + polygonChanges.length;
      alert(`${totalChanges} change(s) saved successfully!`);
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes. Please try again.');
    }
  }, [pendingTextChanges, pendingPolygonChanges, performPatch, queryClient, selectedLayoutId, areaTags]);

  // Cancel pending changes
  const handleCancelChanges = useCallback(() => {
    setPendingTextChanges(new Map());
    setPendingPolygonChanges(new Map());
    setTextEditingMode(false);
    setPolygonEditingMode(false);
  }, []);

  // Handle polygon coordinate updates (local only - no server calls)
  const handlePolygonUpdate = useCallback((areaId: number, newCoordinates: number[][]) => {
    setPendingPolygonChanges(prev => {
      const newMap = new Map(prev);
      newMap.set(areaId, newCoordinates);
      return newMap;
    });
  }, []);

  const handleDeleteAreaTag = useCallback(
    async (areaId: number) => {
      const area = areaTags.find((tag) => tag.id === areaId);
      const areaLabel = area ? getAreaLabel(area) : `Area ${areaId}`;
      const shouldDelete = window.confirm(`Delete "${areaLabel}"? This cannot be undone.`);
      if (!shouldDelete) return;

      try {
        await deleteAreaTagMutation.mutateAsync(areaId);
      } catch (error) {
        alert('Failed to delete area. Please try again.');
      }
    },
    [areaTags, deleteAreaTagMutation, getAreaLabel]
  );

  // Calculate which markers belong to areas and area-photo mappings
  const { markersInAreas, areaPhotoMap } = useMemo(() => {
    const markersInAreasSet = new Set<PhotoMarker>();
    const areaPhotoMapData = new Map<number, number[]>();

    areaTags.forEach((area) => {
      const areaPhotoIds: number[] = [];

      // Get all photo IDs from the area's images
      Object.values(area.images).forEach((photos) => {
        photos.forEach((photo) => {
          areaPhotoIds.push(photo.id);
        });
      });

      areaPhotoMapData.set(area.id, areaPhotoIds);

      // Find markers that have photos matching this area
      displayPhotoMarkers.forEach((marker) => {
        const hasMatchingPhoto = marker.photos.some((photo) => areaPhotoIds.includes(photo.id));
        if (hasMatchingPhoto) {
          markersInAreasSet.add(marker);
        }
      });
    });

    return {
      markersInAreas: markersInAreasSet,
      areaPhotoMap: areaPhotoMapData,
    };
  }, [areaTags, displayPhotoMarkers]);

  // Handle going back to overview (zoom out + deselect area)
  const handleBackToOverview = () => {
    // Reset zoom and position
    reset();
    // Deselect the area
    setSelectedUnit(null);
  };

  // Unit navigation logic
  const sortedUnits = useMemo(() => {
    return [...areaTags].sort((a, b) => {
      // Sort by unit name/number for consistent navigation
      const aName = Object.keys(a.images || {})[0] || `Area ${a.id}`;
      const bName = Object.keys(b.images || {})[0] || `Area ${b.id}`;

      // Extract numbers for natural sorting (e.g., "10" comes after "2")
      const aNum = parseInt(aName.match(/\d+/)?.[0] || '0');
      const bNum = parseInt(bName.match(/\d+/)?.[0] || '0');

      return aNum - bNum;
    });
  }, [areaTags]);

  const currentUnitIndex = useMemo(() => {
    if (!selectedUnit) return -1;
    return sortedUnits.findIndex((unit) => unit.id === selectedUnit.id);
  }, [selectedUnit, sortedUnits]);

  // Enhanced area click handler for zoom and detail view
  const handleAreaClick = useCallback(
    (area: Unit, event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation();
      }

      if (mode === 'view') {
        // Calculate area bounds
        const areaPoints = area.points;
        const minX = Math.min(...areaPoints.map((p) => p[0]));
        const maxX = Math.max(...areaPoints.map((p) => p[0]));
        const minY = Math.min(...areaPoints.map((p) => p[1]));
        const maxY = Math.max(...areaPoints.map((p) => p[1]));

        const areaCenterX = (minX + maxX) / 2;
        const areaCenterY = (minY + maxY) / 2;

        // Calculate aggressive zoom to focus on the area
        const targetScale = Math.min(4, Math.max(2.5, scale * 2.5));

        // Calculate viewport center
        const viewportCenterX = imageDimensions.width / 2;
        const viewportCenterY = imageDimensions.height / 2;

        // Calculate new position to center the area
        const newX = viewportCenterX - areaCenterX * targetScale;
        const newY = viewportCenterY - areaCenterY * targetScale;

        // Apply zoom and center position
        zoom(targetScale / scale);
        setPosition({ x: newX, y: newY });

        // Select the area to show its markers - create a mock event if needed
        const mockEvent =
          event ||
          ({
            stopPropagation: () => {},
            preventDefault: () => {},
          } as React.MouseEvent);
        handleUnitClick(area, mockEvent);
      }
    },
    [mode, scale, imageDimensions.width, imageDimensions.height, zoom, setPosition, handleUnitClick]
  );

  // Navigate to previous unit
  const handlePreviousUnit = useCallback(() => {
    if (sortedUnits.length === 0) return;

    const prevIndex = currentUnitIndex <= 0 ? sortedUnits.length - 1 : currentUnitIndex - 1;

    const prevUnit = sortedUnits[prevIndex];
    if (prevUnit) {
      handleAreaClick(prevUnit);
    }
  }, [sortedUnits, currentUnitIndex, handleAreaClick]);

  // Navigate to next unit
  const handleNextUnit = useCallback(() => {
    if (sortedUnits.length === 0) return;

    const nextIndex = currentUnitIndex >= sortedUnits.length - 1 ? 0 : currentUnitIndex + 1;

    const nextUnit = sortedUnits[nextIndex];
    if (nextUnit) {
      handleAreaClick(nextUnit);
    }
  }, [sortedUnits, currentUnitIndex, handleAreaClick]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys when a unit is selected and we're in view mode
      if (!selectedUnit || mode !== 'view' || sortedUnits.length <= 1) return;

      // Prevent default if we're handling the key
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();

        if (event.key === 'ArrowLeft') {
          handlePreviousUnit();
        } else if (event.key === 'ArrowRight') {
          handleNextUnit();
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedUnit, mode, sortedUnits.length, handlePreviousUnit, handleNextUnit]);

  // Get selected layout
  const selectedLayout = useMemo(() => {
    return layouts.find((l) => String(l.id) === selectedLayoutId);
  }, [layouts, selectedLayoutId]);

  // Check if selected layout has a photo or PDF
  const hasLayoutPhoto = useMemo(() => {
    if (!selectedLayout) return true; // Hide button if no layout selected
    
    // Check for image (must be truthy and not empty string)
    const hasPhoto = Boolean(
      (selectedLayout.image_url && selectedLayout.image_url.trim()) ||
      (selectedLayout.signed_photo_url && selectedLayout.signed_photo_url.trim()) ||
      (selectedLayout.photo_url && selectedLayout.photo_url.trim())
    );
    
    // Check for PDF
    const hasPdf = Boolean(selectedLayout.has_pdf && selectedLayout.pdf_file_url && selectedLayout.pdf_file_url.trim());
    
    console.log('[LayoutsView] hasLayoutPhoto check:', {
      layoutId: selectedLayout.id,
      hasPhoto,
      hasPdf,
      image_url: selectedLayout.image_url,
      pdf_file_url: selectedLayout.pdf_file_url,
      has_pdf: selectedLayout.has_pdf,
    });
    
    return hasPhoto || hasPdf;
  }, [selectedLayout]);

  const handlePhotoUploadSuccess = useCallback(async () => {
    console.log('[LayoutsView] Photo/PDF uploaded, refreshing layouts...');
    
    // Invalidate all layouts cache for this project (matches any type)
    await queryClient.invalidateQueries({ 
      queryKey: ['layouts', contextProjectId],
      exact: false, // This will match ['layouts', projectId, type] queries
    });
    
    // Small delay to ensure the backend has processed the upload
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Refetch the current layouts query
    await queryClient.refetchQueries({
      queryKey: ['layouts', contextProjectId],
      exact: false,
    });
    
    console.log('[LayoutsView] Layouts refetched, loadLayout should trigger automatically');
    
    // The refetch should trigger the useEffect that loads the layout
    // when the layouts data changes (line 1403-1408 in useInteractiveViewer.ts)
    
    setShowAddPhotoDialog(false);
  }, [selectedLayoutId, queryClient, contextProjectId]);

  useEffect(() => {
    if (!isSuperuser && mode === 'areaTags') {
      setMode('view');
    }
  }, [isSuperuser, mode, setMode]);

  useEffect(() => {
    const wasAreaTags = previousModeRef.current === 'areaTags';
    const enteredAreaTags = mode === 'areaTags' && !wasAreaTags;

    if (enteredAreaTags) {
      // Match Home-button behavior when entering area tag editing.
      reset();
      setSelectedUnit(null);
    }

    previousModeRef.current = mode;
  }, [mode, reset, setSelectedUnit]);

  // Early return if no layouts - show simple message
  if (!layoutsLoading && layouts.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-muted-foreground mb-4">
            <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Layouts Yet</h3>
          <p className="text-sm text-muted-foreground">This project doesn't have any layouts yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <TopControls
        selectedLayout={selectedLayoutId}
        setSelectedLayout={setSelectedLayoutId}
        layouts={layouts}
        loadLayout={loadLayout}
        zoom={zoom}
        reset={reset}
        mode={mode}
        setMode={setMode}
        photoIndexMode={photoIndexMode}
        setCurrentPolygon={setCurrentPolygon}
        closePolygon={closePolygon}
        clearPolygon={clearPolygon}
        savePhotoMarkers={savePhotoMarkers}
        setPhotoIndexMode={setPhotoIndexMode}
        setSelectedMarkers={setSelectedMarkers}
        setShowMarkerToolbar={setShowMarkerToolbar}
        svgRef={svgRef}
        isPdfLayout={isPdfLayout}
        pdfPath={pdfPath}
        photoMarkers={displayPhotoMarkers}
        scale={scale}
      />

      {/* Instructions Bar - Second line under top bar, only in areaTags mode */}
      {mode === 'areaTags' && (
        <div className="bg-card border-b border-border px-4 py-2">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs sm:text-sm text-muted-foreground text-center leading-relaxed">
              <span className="hidden lg:inline">
                {areaTags.length > 0
                  ? 'Click to place points • 🟠 Snap to vertices • 🟡 Snap to edges • 🟢 Close area • Esc to cancel'
                  : 'Click to place points • 🟢 Close area • Esc to cancel • Create areas to enable edge snapping'}
              </span>
              <span className="hidden sm:inline lg:hidden">
                {areaTags.length > 0
                  ? 'Click to place • 🟠 Vertices • 🟡 Edges • 🟢 Close • Esc cancel'
                  : 'Click to place • 🟢 Close • Esc cancel'}
              </span>
              <span className="sm:hidden">
                {areaTags.length > 0 ? 'Tap • 🟠🟡🟢 • Esc' : 'Tap to place • 🟢 Close'}
              </span>
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 relative overflow-hidden" style={{ zIndex: 1 }}>
        {/* Add Layout Photo Button Overlay - OUTSIDE MainContentArea */}
        {(() => {
          const showButton = !hasLayoutPhoto && selectedLayout && !selectedUnit && mode !== 'areaTags' && !imagePath && !pdfPath;
          console.log('[LayoutsView] Add button visibility check:', {
            hasLayoutPhoto,
            selectedLayout: selectedLayout?.id,
            selectedUnit: selectedUnit?.id,
            mode,
            imagePath,
            pdfPath,
            showButton,
          });
          return showButton;
        })() && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/90" style={{ zIndex: 10 }}>
            <Button
              onClick={() => {
                setShowAddPhotoDialog(true);
              }}
              className="shadow-2xl bg-blue-600 hover:bg-blue-700 text-white"
              size="lg"
            >
              <ImagePlus className="mr-2 h-5 w-5" />
              Add Layout Photo
            </Button>
          </div>
        )}
        
        {/* Main Layout View - Conditional rendering based on layout type */}
        {isPdfLayout && pdfPath ? (
          <PDFLayoutViewer
            pdfUrl={pdfPath}
            pageNumber={pdfPageNumber}
            cropRegion={pdfCropRegion}
            onDimensionsLoad={setImageDimensions}
            onFitComplete={(fitScale, fitPos) => {
              pdfFitRef.current = { scale: fitScale, position: fitPos };
              setScale(fitScale);
              setPosition(fitPos);
              onPdfFitComplete?.(fitScale, fitPos);
            }}
            scale={scale}
            position={position}
            onScaleChange={setScale}
            onPositionChange={setPosition}
            mode={mode}
            photoIndexMode={photoIndexMode}
            addPhotoMarker={addPhotoMarker}
            addMeasurementPoint={addMeasurementPoint}
            setSelectedMarkers={setSelectedMarkers}
            units={areaTags}
            photoMarkers={displayPhotoMarkers}
            svgRef={svgRef}
            groupRef={groupRef}
            selectedUnit={selectedUnit}
            currentPolygon={currentPolygon}
            selectedMarkers={selectedMarkers}
            markersInAreas={markersInAreas}
            areaPhotoMap={areaPhotoMap}
            showCoordinates={showCoordinates}
            handleUnitClick={handleAreaClick}
            handleMarkerClick={handleMarkerClick}
            deletePhotoMarker={deletePhotoMarker}
            handleMarkerRightClick={handleMarkerRightClick}
            handleMarkerDragStart={handleMarkerDragStart}
            handleSvgClick={handleSvgClick}
          />
        ) : (
          <MainContentArea
            selectedLayoutId={selectedLayoutId}
            getCursor={getCursor}
            imageDimensions={imageDimensions}
            handleMouseDown={handleMouseDown}
            handleSvgClick={handleSvgClick}
            units={areaTags}
            photoMarkers={displayPhotoMarkers}
            svgRef={svgRef}
            groupRef={groupRef}
            position={position}
            scale={scale}
            imagePath={imagePath}
            selectedUnit={selectedUnit}
            currentPolygon={currentPolygon}
            selectedMarkers={selectedMarkers}
            markersInAreas={markersInAreas}
            areaPhotoMap={areaPhotoMap}
            photoIndexMode={photoIndexMode}
            showCoordinates={showCoordinates}
            handleUnitClick={handleAreaClick}
            handleMarkerClick={handleMarkerClick}
            deletePhotoMarker={deletePhotoMarker}
            handleMarkerRightClick={handleMarkerRightClick}
            handleMarkerDragStart={handleMarkerDragStart}
            onWheelZoom={onWheelZoom}
            handleTouchStart={handleTouchStart}
            handleTouchMove={handleTouchMove}
            handleTouchEnd={handleTouchEnd}
            disableMarkerClicks={mode === 'areaTags'}
            textEditingMode={textEditingMode}
            onRequestLabelEdit={openLabelDialogForArea}
            pendingTextChanges={pendingTextChanges}
            onSaveTextChanges={handleSaveChanges}
            onCancelTextChanges={handleCancelChanges}
            polygonEditingMode={polygonEditingMode}
            onPolygonUpdate={handlePolygonUpdate}
            pendingPolygonChanges={pendingPolygonChanges}
            showAreaDeleteControls={isSuperuser && mode === 'areaTags'}
            onDeleteAreaTag={handleDeleteAreaTag}
          />
        )}

        {/* Area Tags Drawing Overlay - Only show when in area tags mode */}
        {mode === 'areaTags' && isSuperuser && (
          <EnhancedAreaTagsOverlay
            selectedLayoutId={selectedLayoutId}
            projectId={contextProjectId}
            imageDimensions={imageDimensions}
            position={position}
            scale={scale}
            svgRef={svgRef}
            groupRef={groupRef}
            onWheelZoom={onWheelZoom}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTextEditingModeChange={handleTextEditingModeChange}
            onSaveTextChanges={handleSaveChanges}
            onCancelTextChanges={handleCancelChanges}
            onPolygonEditingModeChange={handlePolygonEditingModeChange}
            pendingPolygonChanges={pendingPolygonChanges}
          />
        )}

        {/* Back to Overview Button - Show when area is selected */}
        {selectedUnit && mode !== 'areaTags' && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30">
            <Button
              onClick={handleBackToOverview}
              variant="default"
              size="lg"
              className="bg-card text-foreground border-2 border-border hover:bg-accent shadow-lg"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Overview
              <ZoomOut className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Loading overlays */}
        {layoutsLoading && (
          <div className="absolute inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center z-50">
            <LogoLoading 
              variant="breathing" 
              size="lg" 
              message="Loading layouts..." 
            />
          </div>
        )}
        {layoutDataLoading && (
          <div className="absolute inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center z-50">
            <LogoLoading 
              variant="breathing" 
              size="lg" 
              message="Loading layout..." 
            />
          </div>
        )}
      </div>

      {/* StatusBar - only shown on Layouts tab */}
      <StatusBar
        mode={mode}
        scale={scale}
        photoIndexMode={photoIndexMode}
        selectedMarkers={selectedMarkers}
        savePhotoMarkersMutation={savePhotoMarkersMutation}
        currentPolygon={currentPolygon}
        showCoordinates={showCoordinates}
        isHorizontalConstrained={isHorizontalConstrained}
        isVerticalConstrained={isVerticalConstrained}
        setShowCoordinates={setShowCoordinates}
      />

      {/* Add Layout Photo Dialog */}
      {selectedLayout && (
        <AddLayoutPhotoDialog
          open={showAddPhotoDialog}
          layoutId={selectedLayoutId}
          layoutName={selectedLayout.name}
          projectId={contextProjectId}
          onOpenChange={setShowAddPhotoDialog}
          onSuccess={handlePhotoUploadSuccess}
        />
      )}

      <Dialog open={isLabelDialogOpen} onOpenChange={(open) => (!open ? closeLabelDialog() : setIsLabelDialogOpen(true))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Unit Label</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isSavingLabel) {
                  handleLabelDialogSave();
                }
              }}
              placeholder="Enter unit label"
              autoFocus
              disabled={isSavingLabel}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeLabelDialog} disabled={isSavingLabel}>
              Cancel
            </Button>
            <Button onClick={handleLabelDialogSave} disabled={isSavingLabel || !draftLabel.trim()}>
              {isSavingLabel ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LayoutsView;
